import React, { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { fetchCmsItemBySlug, getOptimizedImageUrl } from '../lib/cms'
import InquiryModal from '../components/InquiryModal'

export default function DestinationDetailPage() {
  const { slug } = useParams()
  const [destination, setDestination] = useState(null)
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)

  useEffect(() => {
    async function loadData() {
      setLoading(true)
      const destData = await fetchCmsItemBySlug('destinationer', slug)
      if (destData) {
        setDestination(destData)
        document.title = `${destData.destinationsnavn} - SKI & CO TRAVEL ApS`
      } else {
        document.title = 'Destination ikke fundet - SKI & CO TRAVEL ApS'
      }
      setLoading(false)
    }
    loadData()
  }, [slug])

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center text-slate-400">
        <i className="fa-solid fa-spinner animate-spin text-3xl text-cyan-400 mb-3" aria-hidden="true" />
        <p>Henter destinationsoplysninger...</p>
      </div>
    )
  }

  if (!destination) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center space-y-6">
        <h1 className="font-heading font-extrabold text-3xl text-white">
          Destinationen blev ikke fundet
        </h1>
        <Link
          to="/destinationer"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-cyan-500 text-slate-950 font-bold text-sm"
        >
          <i className="fa-solid fa-arrow-left" aria-hidden="true" />
          <span>Tilbage til destinationer</span>
        </Link>
      </div>
    )
  }

  const imageUrl = Array.isArray(destination.hero_billede)
    ? destination.hero_billede[0]
    : destination.hero_billede

  return (
    <div className="space-y-12 pb-16">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6">
        <nav className="flex items-center gap-2 text-xs text-slate-400">
          <Link to="/" className="hover:text-cyan-400">Forside</Link>
          <span>/</span>
          <Link to="/destinationer" className="hover:text-cyan-400">Destinationer</Link>
          <span>/</span>
          <span className="text-slate-200 font-semibold">{destination.destinationsnavn}</span>
        </nav>
      </div>

      {/* Hero Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="relative rounded-3xl overflow-hidden border border-slate-800 bg-slate-900 shadow-2xl">
          <div className="relative h-[380px] lg:h-[460px]">
            <img
              src={getOptimizedImageUrl(imageUrl, 1600, 85)}
              alt={destination.destinationsnavn}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent" />

            <div className="absolute top-4 left-4">
              <span className="bg-slate-950/80 backdrop-blur-md text-cyan-300 text-xs font-semibold px-3 py-1 rounded-full border border-slate-700">
                <i className="fa-solid fa-earth-europe mr-1.5" aria-hidden="true" />
                {destination.land}
              </span>
            </div>

            <div className="absolute bottom-6 left-6 right-6 lg:bottom-10 lg:left-10 lg:right-10 space-y-2">
              <h1 className="font-heading font-extrabold text-2xl sm:text-4xl text-white">
                {destination.destinationsnavn}
              </h1>
              <p className="text-slate-200 text-base max-w-2xl">
                {destination.kort_beskrivelse}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Grid: Description + Facts Sidebar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Detailed text */}
          <div className="lg:col-span-8 space-y-8">
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 sm:p-8 space-y-4">
              <h2 className="font-heading font-bold text-xl text-white">
                Om destinationen & skiløbet
              </h2>

              {destination.lang_beskrivelse && (
                <div
                  className="prose prose-invert prose-cyan text-slate-300 text-sm leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: destination.lang_beskrivelse }}
                />
              )}
            </div>
          </div>

          {/* Facts Box */}
          <div className="lg:col-span-4 space-y-6">
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-6 shadow-xl">
              <h3 className="font-heading font-bold text-xl text-white flex items-center gap-2">
                <i className="fa-solid fa-circle-info text-cyan-400" aria-hidden="true" />
                <span>Fakta om områderne</span>
              </h3>

              {destination.fakta ? (
                <div
                  className="prose prose-invert text-slate-300 text-xs sm:text-sm leading-relaxed space-y-2"
                  dangerouslySetInnerHTML={{ __html: destination.fakta }}
                />
              ) : (
                <p className="text-slate-400 text-xs">
                  Ring til os for mere fakta om pister, højder og lifter på {destination.destinationsnavn}.
                </p>
              )}

              <div className="pt-4 border-t border-slate-800 space-y-3">
                <button
                  onClick={() => setModalOpen(true)}
                  className="w-full py-3 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold text-sm transition-colors"
                >
                  Forespørg på rejse hertil
                </button>
                <a
                  href="tel:+4551589452"
                  className="w-full py-3 rounded-xl bg-slate-800 hover:bg-slate-750 text-white font-semibold text-sm transition-colors border border-slate-700 flex items-center justify-center gap-2"
                >
                  <i className="fa-solid fa-phone text-cyan-400" aria-hidden="true" />
                  <span>+45 51 58 94 52</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <InquiryModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        selectedDestination={destination.destinationsnavn}
      />
    </div>
  )
}
